import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, ArrowLeft, Mail, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { FloatingLanguageSelector } from '../components/LanguageSelector';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import api from '../lib/api';
import { usePageTitle } from '../hooks/usePageTitle';

export default function ForgotPasswordPage() {
  const { t } = useTranslation(['auth', 'common']);
  // BLOG-1-CIERRE (S6): la pestana en el idioma del usuario. Ver `hooks/usePageTitle.js`.
  usePageTitle(t('auth:forgot_password.meta_title'));
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error(t('forgot_password.toasts.enter_email'));
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setSubmitted(true);
      toast.success(t('forgot_password.toasts.sent'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('forgot_password.toasts.send_failed_fallback'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#0e0e0e]">
      <FloatingLanguageSelector />
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <Link to="/" className="inline-block mb-8 font-['Space_Grotesk'] font-bold text-xl tracking-tight">
            Quiz<span className="text-[#FF6B00]">essa</span>
          </Link>
          <h1 className="text-3xl font-bold text-white" data-testid="forgot-password-title">{t('forgot_password.title')}</h1>
          <p className="text-[#6a6a6a] mt-2">{t('forgot_password.subtitle')}</p>
        </div>

        {submitted ? (
          <div className="space-y-6 text-center" data-testid="forgot-success-state">
            <div className="flex flex-col items-center gap-4 p-8 rounded-2xl border border-emerald-500/20 bg-emerald-500/5">
              <CheckCircle2 className="w-12 h-12 text-emerald-400" />
              <h2 className="text-xl font-semibold text-white">{t('forgot_password.success.title')}</h2>
              <p className="text-[#6a6a6a] text-sm">
                {t('forgot_password.success.body_prefix')} <span className="text-white">{email}</span>
              </p>
              <p className="text-[#6a6a6a] text-xs">{t('forgot_password.success.spam_hint')}</p>
            </div>
            <Button
              variant="ghost"
              onClick={() => { setSubmitted(false); setEmail(''); }}
              className="text-[#FF6B00] hover:text-[#FF8c3e]"
              data-testid="forgot-try-another-btn">
              {t('forgot_password.success.try_another_btn')}
            </Button>
            <Link to="/login" className="flex items-center justify-center gap-2 text-[#6a6a6a] hover:text-white transition" data-testid="forgot-back-to-login">
              <ArrowLeft className="w-4 h-4" /> {t('forgot_password.back_to_login')}
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="forgot-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#cccccc]">{t('forgot_password.form.email_label')}</Label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6a6a6a]" />
                <Input
                  id="email" type="email" name="email" autoComplete="username"
                  placeholder={t('forgot_password.form.email_placeholder')}
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-[#181818] border-[#2a2a2a] text-white"
                  data-testid="forgot-email-input"
                />
              </div>
            </div>

            <Button type="submit" disabled={loading}
              className="w-full bg-[#FF6B00] hover:bg-[#FF8c3e] text-white font-semibold"
              data-testid="forgot-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t('forgot_password.form.submit_btn')}
            </Button>

            <Link to="/login" className="flex items-center justify-center gap-2 text-[#6a6a6a] hover:text-white transition text-sm" data-testid="forgot-back-link">
              <ArrowLeft className="w-4 h-4" /> {t('forgot_password.back_to_login')}
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
